/**
 * REVERTING A CARD CHANGE (KPR-704; keepr-api docs/SCHEMA.md § Change card,
 * "Reverting a card change"). A History entry of a card change
 * (`item.changeCard`) reverts whole: the item goes back to the card it was
 * changed from, with every value the change took away. These are the words
 * the revert dialog and its notice use, kept apart so they are read and
 * tested in one place.
 */

/** A card change the History door reverts (not a revert of one, which never is). */
export function isCardChangeEntry( entry ) {
	return entry?.action === 'item.changeCard' && ! entry?.revert;
}

const quoted = name => ( name ? `“${ name }”` : 'the card it came from' );
const list = ( names ) => {
	const shown = names.map( n => `“${ n }”` );
	if ( shown.length <= 1 ) return shown.join( '' );
	return `${ shown.slice( 0, -1 ).join( ', ' ) } and ${ shown[ shown.length - 1 ] }`;
};
const plural = ( n, one, many ) => `${ n } ${ n === 1 ? one : many }`;

/** What the dialog says will happen, before anything does. */
export function cardChangeRevertExplanation( entry ) {
	const from = entry?.cardChange?.from?.name;
	const to = entry?.cardChange?.to?.name;
	return [
		`The item goes back to ${ quoted( from ) } with every value it had there, including the ones this change left in a comment or lost.`,
		`Values entered on ${ to ? `“${ to }”` : 'its current card' } since this change are kept in History, not on the item.`,
		'The comment this change wrote is removed, unless someone edited it.',
	];
}

/**
 * What the revert did beyond the move back, as sentences for its notice
 * (keepr-api `cardChange` in the revert's answer). Empty when there is
 * nothing more to say.
 */
export function cardChangeRevertNotices( cardChange ) {
	if ( ! cardChange ) return [];
	const lines = [];
	const edited = ( Array.isArray( cardChange.editedAfter ) ? cardChange.editedAfter : [] ).map( e => e?.label || e?.element ).filter( Boolean );
	if ( edited.length ) {
		const on = cardChange.from?.name ? ` on “${ cardChange.from.name }”` : '';
		lines.push( `${ list( edited ) } ${ edited.length === 1 ? 'was' : 'were' } changed${ on } after the card change. ${ edited.length === 1 ? 'That value is' : 'Those values are' } kept in History.` );
	}
	const missing = cardChange.notRestored || {};
	const count = Number( missing.count ) || 0;
	if ( count ) {
		const named = Array.isArray( missing.elements ) && missing.elements.length ? ` (${ list( missing.elements ) })` : '';
		lines.push( `${ plural( count, 'value was', 'values were' ) } not put back${ named }: ${ quoted( cardChange.to?.name ) } no longer has ${ count === 1 ? 'its element' : 'their elements' }. ${ count === 1 ? 'It stays' : 'They stay' } in History.` );
	}
	const lost = Number( cardChange.files?.lost ) || 0;
	if ( lost ) lines.push( `${ plural( lost, 'file', 'files' ) } could not come back: ${ lost === 1 ? 'its contents are' : 'their contents are' } gone.` );
	const kept = Number( cardChange.comments?.kept ) || 0;
	if ( kept ) lines.push( `The change’s comment was edited since, so ${ kept === 1 ? 'it stays' : 'those parts stay' }.` );
	return lines;
}
